import { SubjectRawRule, ExtractSubjectType, PureAbility } from "@casl/ability";
import { unpackRules, PackRule } from "@casl/ability/extra";
import type { AppSubjects } from "@lbcrm-api/casl/app.subjects";
import axios, { Axios, isAxiosError } from "axios";
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Action } from "src/casl/Action";
import { AblePerson, AuthenticationContext } from "./AuthenticationContext";
import { AbilityContext } from "./Can";
import { API_URL } from "src/core/constants";
import { toast } from "react-toastify";
import { LoadingScreen } from "src/core/components/LoadingScreen";
import { PrismaQuery, createPrismaAbility } from "@casl/prisma";
import { useAuthToken } from "../useAuthToken";
import { usePostHog } from "posthog-js/react";

interface Props {}

export const createAxios = () => {
    return axios.create({
        baseURL: API_URL,
        headers: {
            "X-Client-Version": import.meta.env.VITE_RELEASE_VERSION || "dev",
        },
    });
};

export const defaultAxios = createAxios();

export const AuthenticationProvider = (props: PropsWithChildren<Props>) => {
    const posthog = usePostHog();

    const [initialised, setInitialised] = useState(false);
    const initialising = useRef(false);
    const [person, setPerson] = useState<AblePerson | null>();
    const [hasDuties, setHasDuties] = useState(false);
    const [ability, setAbility] = useState<PureAbility<[Action, AppSubjects], PrismaQuery>>();
    const [authenticationError, setAuthenticationError] = useState<Error>();

    const [authToken, setAuthToken] = useAuthToken();

    const axiosRef = useRef<Axios | undefined>(defaultAxios);

    const navigate = useNavigate();

    const logout = useCallback(
        async (recordFromState = false) => {
            setAuthToken(null);

            navigate("/login" + (recordFromState ? `?from=${encodeURIComponent(window.location.pathname)}` : ""));

            setPerson(undefined);
            setHasDuties(false);
            setAuthenticationError(undefined);

            axiosRef.current = undefined;
        },
        [navigate, setAuthToken]
    );

    const getProfile = useCallback(
        async (token: string) => {
            try {
                const response = await axios.get<{
                    person?: AblePerson;
                    hasDuties: boolean;
                    rules: PackRule<SubjectRawRule<Action, ExtractSubjectType<AppSubjects>, PrismaQuery>>[];
                }>(API_URL + "/profile", {
                    headers: {
                        Authorization: "Bearer " + token,

                        "X-Client-Version": import.meta.env.VITE_RELEASE_VERSION || "dev",
                    },
                });

                const person = response.data.person;
                const rules = response.data.rules;

                axiosRef.current = createAxios();

                axiosRef.current.interceptors.request.use(
                    async (config) => {
                        if (config.headers) {
                            config.headers["Authorization"] = "Bearer " + token;
                        }

                        return config;
                    },
                    (error) => {
                        return Promise.reject(error);
                    }
                );

                axiosRef.current.interceptors.response.use(
                    (response) => {
                        return response;
                    },
                    async (error) => {
                        // Sign out if a 401 was received
                        if (isAxiosError(error) && error.response?.status === 401) {
                            await logout(true);
                        }

                        return Promise.reject(error);
                    }
                );

                setPerson(person);
                setHasDuties(response.data.hasDuties);

                const ability = createPrismaAbility<[Action, AppSubjects], PrismaQuery>(unpackRules(rules));

                // @ts-expect-error - missing private properties
                setAbility(ability);

                if (posthog && person) {
                    posthog.identify(person.id, {
                        name: person.firstName + " " + person.lastName,
                        email: person.email,
                        phone: person.phone,
                    });
                }
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    if (error.response?.status === 401) {
                        await logout(true);
                    }

                    setAuthenticationError(error);
                } else if (error instanceof Error) {
                    setAuthenticationError(error);
                } else {
                    setAuthenticationError(
                        new Error(
                            "An unknown error occurred while logging in. Please try again shortly, or contact someone at church."
                        )
                    );
                }
            }
        },
        [logout, posthog]
    );

    // Attempts to authenticate the user using the token in local storage
    useEffect(() => {
        if (initialised || initialising.current) {
            return;
        }

        async function initialise() {
            try {
                if (authToken) {
                    await getProfile(authToken);
                }
            } catch {
                // TODO handle any initialisation error. This should probably clear the screen
            } finally {
                setInitialised(true);
                initialising.current = false;
            }
        }

        initialising.current = true;
        initialise();
    }, [initialised, getProfile, authToken]);

    const handleLogin = async (newToken: string) => {
        if (!newToken) {
            toast.error("No authentication token provided. Please try logging in again or contact church for help.");
            return;
        }

        setAuthToken(newToken);

        try {
            await getProfile(newToken);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                setAuthenticationError(error);
            } else if (error instanceof Error) {
                setAuthenticationError(error);
            } else {
                setAuthenticationError(
                    new Error(
                        "An unknown error occurred while logging in. Please try again shortly, or contact someone at church."
                    )
                );
            }
        }
    };

    const refreshProfile = async () => {
        try {
            if (!authToken) {
                toast.error("You are not authenticated. Please log in again.");

                return;
            }

            await getProfile(authToken);
        } catch {
            toast.error("An error occurred while refreshing your profile. Please try again shortly.");
        }
    };

    if (!initialised) {
        return <LoadingScreen img />;
    }

    return (
        <AuthenticationContext.Provider
            value={{
                person,
                hasDuties,
                error: authenticationError,
                client: axiosRef.current,
                handleLogin,
                logout,
                refreshProfile,
            }}
        >
            {ability ? (
                <AbilityContext.Provider value={ability}>{props.children}</AbilityContext.Provider>
            ) : (
                props.children
            )}
        </AuthenticationContext.Provider>
    );
};
